import { cx } from 'classix'
import { useDocumentDirection } from 'hooks/useDocumentDirection/useDocumentDirection'
import {
	Children,
	forwardRef,
	type ReactNode,
	type Ref,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from 'react'

import styles from './Steps.module.css'

export interface StepsContext<T = unknown> {
	lastIndex: number | null
	/** To share date between steps */
	detail: T
}

interface StepsProps<T> {
	onNext?: (index: number) => void
	onPrev?: (index: number) => void
	onChange?: (index: number) => void
	'data-testid'?: string
	children: (
		next: () => void,
		prev: () => void,
		ctx: StepsContext<T>
	) => ReactNode[]
}

const NO_ANIMATION = ''

export interface StepsApi<T> {
	next: () => void
	prev: () => void
	ctx: StepsContext<T>
}

export function StepsComponent<T>(
	{
		children,
		onNext,
		onPrev,
		onChange,
		'data-testid': dataTestId,
	}: Readonly<StepsProps<T>>,
	ref: Ref<StepsApi<T>>
) {
	const { isRtl } = useDocumentDirection()
	const [index, setIndex] = useState(0)
	const stepsCtx = useRef<StepsContext<T>>({ lastIndex: null, detail: {} as T })
	const innerRef = useRef<HTMLDivElement>(null)
	const [animation, setAnimation] = useState<string>('')

	const next = useCallback(() => {
		setAnimation(styles.next)
		onNext?.(index)
		innerRef.current?.addEventListener(
			'animationend',
			() => {
				setIndex((prevIndex) => {
					stepsCtx.current.lastIndex = prevIndex
					return prevIndex + 1
				})
				setAnimation(NO_ANIMATION)
			},
			{ once: true }
		)
	}, [index, onNext])

	const prev = useCallback(() => {
		setAnimation(styles.prev)
		onPrev?.(index)
		innerRef.current?.addEventListener(
			'animationend',
			() => {
				setIndex((prevIndex) => {
					stepsCtx.current.lastIndex = prevIndex
					return prevIndex - 1
				})
				setAnimation(NO_ANIMATION)
			},
			{ once: true }
		)
	}, [index, onPrev])

	useImperativeHandle(ref, () => ({ next, prev, ctx: stepsCtx.current }), [
		next,
		prev,
	])

	const steps = children(next, prev, stepsCtx.current)
	const max = Children.count(steps) - 1

	const currentStep = useMemo(
		() => steps[Math.max(0, Math.min(index, max))],
		[index, max, steps]
	)

	const prevStep = useMemo(() => {
		const prevIndex = Math.max(-1, Math.min(index - 1, max))
		if (prevIndex < 0 || animation !== styles.prev) {
			return null
		}
		return steps[prevIndex]
	}, [index, max, animation, steps])

	const nextStep = useMemo(() => {
		const nextIndex = Math.max(0, Math.min(index + 1, max + 1))
		if (nextIndex > max || animation !== styles.next) {
			return null
		}
		return steps[nextIndex]
	}, [index, max, animation, steps])

	useEffect(() => {
		onChange?.(index)
	}, [index, onChange])

	return (
		<div data-steps-container className={styles.steps} data-testid={dataTestId}>
			<div className={cx(styles.inner, animation)} ref={innerRef}>
				<div className={styles.step}>{isRtl ? nextStep : prevStep}</div>
				<div className={styles.step}>{currentStep}</div>
				<div className={styles.step}>{isRtl ? prevStep : nextStep}</div>
			</div>
		</div>
	)
}

export const Steps = forwardRef(StepsComponent)
